/**
 * Item Store
 *
 * Zustand store for Items Repository (Super Admin)
 * Manages state for Policies, Procedures, Templates, Guidelines, and Resources
 */

import { create } from 'zustand'
import { toast } from 'react-hot-toast'
import itemService from '@services/itemService'

const useItemStore = create((set, get) => ({
  // State
  items: [],
  selectedItem: null,
  versions: [],
  assignments: [],
  loading: false,
  versionsLoading: false,
  assignmentsLoading: false,
  error: null,

  // Filters
  filters: {
    search: '',
    type: 'all',
    status: 'all',
  },

  // Pagination
  pagination: {
    page: 1,
    limit: 25,
    total: 0,
    totalPages: 0,
  },

  // Statistics
  stats: {
    total: 0,
    published: 0,
    drafts: 0,
    archived: 0,
    byType: {},
  },

  // ========================================
  // READ ACTIONS
  // ========================================

  /**
   * Fetch items with current filters and pagination
   */
  fetchItems: async () => {
    const { filters, pagination } = get()
    set({ loading: true, error: null })

    try {
      const response = await itemService.getAll(filters, pagination)
      set({
        items: response.data || [],
        pagination: response.pagination || pagination,
        loading: false,
      })
    } catch (error) {
      console.error('Failed to fetch items:', error)
      set({ error: error.message, loading: false })
      toast.error('Failed to fetch items')
    }
  },

  /**
   * Fetch single item by ID
   */
  fetchItem: async (id) => {
    set({ loading: true, error: null })

    try {
      const response = await itemService.getById(id)
      set({ selectedItem: response.data, loading: false })
      return response.data
    } catch (error) {
      console.error('Failed to fetch item:', error)
      set({ error: error.message, loading: false })
      toast.error('Failed to fetch item')
      return null
    }
  },

  /**
   * Fetch item statistics
   */
  fetchStats: async () => {
    try {
      const response = await itemService.getStats()
      set({ stats: response.data || {} })
    } catch (error) {
      console.error('Failed to fetch stats:', error)
    }
  },

  /**
   * Fetch version history for an item
   */
  fetchVersions: async (id) => {
    set({ versionsLoading: true })

    try {
      const response = await itemService.getVersions(id)
      set({ versions: response.data || [], versionsLoading: false })
      return response.data
    } catch (error) {
      console.error('Failed to fetch versions:', error)
      set({ versionsLoading: false })
      toast.error('Failed to fetch version history')
      return []
    }
  },

  /**
   * Fetch company assignments for an item
   */
  fetchAssignments: async (id) => {
    set({ assignmentsLoading: true })

    try {
      const response = await itemService.getAssignments(id)
      set({ assignments: response.data || [], assignmentsLoading: false })
      return response
    } catch (error) {
      console.error('Failed to fetch assignments:', error)
      set({ assignmentsLoading: false })
      toast.error('Failed to fetch assignments')
      return { data: [], download_stats: {} }
    }
  },

  // ========================================
  // WRITE ACTIONS
  // ========================================

  /**
   * Create new item
   */
  createItem: async (formData, onProgress = null) => {
    set({ loading: true, error: null })

    try {
      const response = await itemService.create(formData, onProgress)
      toast.success('Item created successfully')
      // Refresh the items list to get proper data with all computed fields
      await get().fetchItems()
      await get().fetchStats()
      return response.data
    } catch (error) {
      console.error('Failed to create item:', error)
      set({ error: error.message, loading: false })
      toast.error(error.message || 'Failed to create item')
      return null
    }
  },

  /**
   * Update item
   */
  updateItem: async (id, formData, onProgress = null) => {
    set({ loading: true, error: null })

    try {
      const response = await itemService.update(id, formData, onProgress)
      toast.success('Item updated successfully')
      // Refresh the items list to get proper data with all computed fields
      await get().fetchItems()
      await get().fetchStats()
      // Update selectedItem if it was the one being edited
      if (get().selectedItem?.id === id) {
        set({ selectedItem: response.data })
      }
      return response.data
    } catch (error) {
      console.error('Failed to update item:', error)
      set({ error: error.message, loading: false })
      toast.error(error.message || 'Failed to update item')
      return null
    }
  },

  /**
   * Delete item (soft delete)
   */
  deleteItem: async (id) => {
    set({ loading: true, error: null })

    try {
      await itemService.delete(id)
      toast.success('Item deleted successfully')
      // Refresh the items list to reflect deletion
      await get().fetchItems()
      await get().fetchStats()
      return true
    } catch (error) {
      console.error('Failed to delete item:', error)
      set({ error: error.message, loading: false })
      toast.error(error.message || 'Failed to delete item')
      return false
    }
  },

  /**
   * Restore deleted item
   */
  restoreItem: async (id) => {
    set({ loading: true, error: null })

    try {
      await itemService.restore(id)
      toast.success('Item restored successfully')
      // Refresh the items list to reflect restoration
      await get().fetchItems()
      await get().fetchStats()
      return true
    } catch (error) {
      console.error('Failed to restore item:', error)
      set({ error: error.message, loading: false })
      toast.error(error.message || 'Failed to restore item')
      return false
    }
  },

  /**
   * Rollback to a previous version
   */
  rollbackVersion: async (id, version) => {
    set({ loading: true, error: null })

    try {
      const response = await itemService.rollback(id, version)
      toast.success(`Rolled back to version ${version}`)
      get().fetchItem(id)
      get().fetchVersions(id)
      return response
    } catch (error) {
      console.error('Failed to rollback:', error)
      set({ error: error.message, loading: false })
      toast.error(error.message || 'Failed to rollback version')
      return null
    }
  },

  /**
   * Assign item to companies
   */
  assignToCompanies: async (id, companyIds, version = null) => {
    set({ assignmentsLoading: true })

    try {
      const response = await itemService.assignToCompanies(id, companyIds, version)
      toast.success(response.message || 'Item assigned successfully')
      get().fetchAssignments(id)
      return true
    } catch (error) {
      console.error('Failed to assign:', error)
      set({ assignmentsLoading: false })
      toast.error(error.message || 'Failed to assign item')
      return false
    }
  },

  /**
   * Unassign item from company
   */
  unassignFromCompany: async (id, companyId) => {
    set({ assignmentsLoading: true })

    try {
      await itemService.unassignFromCompany(id, companyId)
      set((state) => ({
        assignments: state.assignments.filter((a) => a.company_id !== companyId),
        assignmentsLoading: false,
      }))
      toast.success('Item unassigned from company')
      return true
    } catch (error) {
      console.error('Failed to unassign:', error)
      set({ assignmentsLoading: false })
      toast.error(error.message || 'Failed to unassign item')
      return false
    }
  },

  /**
   * Download item file
   */
  downloadItem: async (id, version = null) => {
    try {
      await itemService.download(id, version)
      toast.success('Download started')
    } catch (error) {
      console.error('Failed to download:', error)
      toast.error('Failed to download file')
    }
  },

  // ========================================
  // FILTER ACTIONS
  // ========================================

  /**
   * Set search filter
   */
  setSearch: (search) => {
    set((state) => ({
      filters: { ...state.filters, search },
      pagination: { ...state.pagination, page: 1 },
    }))
  },

  /**
   * Set type filter
   */
  setTypeFilter: (type) => {
    set((state) => ({
      filters: { ...state.filters, type },
      pagination: { ...state.pagination, page: 1 },
    }))
  },

  /**
   * Set status filter
   */
  setStatusFilter: (status) => {
    set((state) => ({
      filters: { ...state.filters, status },
      pagination: { ...state.pagination, page: 1 },
    }))
  },

  /**
   * Reset all filters
   */
  resetFilters: () => {
    set({
      filters: {
        search: '',
        type: 'all',
        status: 'all',
      },
      pagination: { page: 1, limit: 25, total: 0, totalPages: 0 },
    })
  },

  // ========================================
  // PAGINATION ACTIONS
  // ========================================

  /**
   * Set current page
   */
  setPage: (page) => {
    set((state) => ({
      pagination: { ...state.pagination, page },
    }))
  },

  /**
   * Set items per page
   */
  setLimit: (limit) => {
    set((state) => ({
      pagination: { ...state.pagination, limit, page: 1 },
    }))
  },

  /**
   * Go to next page
   */
  nextPage: () => {
    const { pagination } = get()
    if (pagination.page < pagination.totalPages) {
      set((state) => ({
        pagination: { ...state.pagination, page: state.pagination.page + 1 },
      }))
    }
  },

  /**
   * Go to previous page
   */
  prevPage: () => {
    const { pagination } = get()
    if (pagination.page > 1) {
      set((state) => ({
        pagination: { ...state.pagination, page: state.pagination.page - 1 },
      }))
    }
  },

  // ========================================
  // SELECTION ACTIONS
  // ========================================

  /**
   * Select an item
   */
  selectItem: (item) => {
    set({ selectedItem: item })
  },

  /**
   * Clear selection
   */
  clearSelection: () => {
    set({ selectedItem: null, versions: [], assignments: [] })
  },

  /**
   * Clear error
   */
  clearError: () => {
    set({ error: null })
  },
}))

export default useItemStore
